import { GoogleLogin } from '@react-oauth/google';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GoogleGlyph } from '@/components/ui/google-glyph';
import { OAuthButton } from '@/components/ui/oauth-button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';

type Props = {
  /** Tailwind class overrides for the outer wrapper. */
  className?: string;
  /** Where to navigate after a successful sign-in. Defaults to `/app`. */
  redirectTo?: '/app' | '/';
  /**
   * When true, do not navigate after sign-in. The auth context update is
   * enough to flip the surrounding page from logged-out to logged-in UI in
   * place — used by inline sign-in banners (e.g. on /pickem/$eventId).
   */
  stayOnPage?: boolean;
  /** Called when GSI returns an error or credential is missing. */
  onError?: (message: string) => void;
  /**
   * Visual size of the button skin.
   * - `default`: h-10 with a 320px cap (used on the dedicated /login card).
   * - `lg`: h-12, stretches to its container — pair with another h-12 CTA
   *   (e.g. the home page Download button) so the two read as a single
   *   stacked group on mobile rather than mismatched.
   */
  size?: 'default' | 'lg';
};

// Friendly messages we're willing to surface. Raw server bodies, JSON blobs,
// and stack traces collapse to a generic line — the detail goes to the
// console for devs.
function toUserFacingError(err: unknown): string {
  const fallback = 'Something went wrong signing you in. Please try again.';
  if (err instanceof Error) {
    const msg = err.message;
    if (/^\{|^Google login failed|\(\d{3}\)/.test(msg)) return fallback;
    if (msg.length <= 120 && !msg.includes('{')) return msg;
    return fallback;
  }
  return fallback;
}

/**
 * Brand-styled "Continue with Google" button that directly triggers the
 * Google Identity Services flow.
 *
 * GSI's rendered button lives inside an iframe that we cannot theme. To keep
 * our visual language intact, we render the shared `OAuthButton` skin and
 * stack the real GSI button on top at `opacity-0` so it still receives the
 * click and runs the OAuth flow. The visible button is purely a skin
 * (pointer-events-none, aria-hidden, tabIndex=-1) so screen readers and
 * keyboards still interact with the real GSI control underneath.
 *
 * Used on both the login page and the home landing so clicking "Sign in
 * with Google" anywhere kicks off the same flow — no page detour.
 */
export function GoogleSignInButton({
  className,
  redirectTo = '/app',
  stayOnPage = false,
  onError,
  size = 'default',
}: Props) {
  const isLg = size === 'lg';
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleSuccess = async (credential?: string) => {
    if (!credential) {
      onError?.('Google did not return a credential. Please try again.');
      return;
    }
    setBusy(true);
    try {
      await loginWithGoogle(credential);
      if (!stayOnPage) {
        navigate({ to: redirectTo, replace: true });
      }
    } catch (err) {
      console.error('Google login failed:', err);
      onError?.(toUserFacingError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(
        'relative w-full',
        isLg ? 'h-12' : 'h-10 max-w-[320px]',
        className,
      )}
      aria-busy={busy}
    >
      <OAuthButton
        icon={<GoogleGlyph className="h-5 w-5" />}
        label="Continue with Google"
        loading={busy}
        // The visible button is decorative — the real click target is the
        // invisible GSI iframe stacked on top. `pointer-events-none` on the
        // skin + `tabIndex={-1}` + `aria-hidden` keeps focus/keyboard/screen-
        // reader interactions on the real control below.
        className={cn(
          'pointer-events-none',
          // Match the wrapper height. OAuthButton hardcodes h-10; in `lg`
          // mode we override to h-12 so the button skin doesn't sit short
          // inside a taller container.
          isLg && 'h-12 text-base font-semibold',
        )}
        tabIndex={-1}
        aria-hidden="true"
      />
      <div className="absolute inset-0 opacity-0 [&>div]:!w-full [&>div]:!h-full [&_iframe]:!w-full [&_iframe]:!h-full">
        <GoogleLogin
          onSuccess={(res) => handleSuccess(res.credential)}
          onError={() =>
            onError?.('Google sign-in was cancelled. Please try again.')
          }
          useOneTap={false}
          theme="filled_black"
          shape="pill"
          size="large"
          text="continue_with"
          width="320"
        />
      </div>
    </div>
  );
}
